import type { PaneChannelTiming, PaneChannelTimings } from './AnimationTimingTypes';
import { IPaneState } from './AnimationsInParallel';
import { ArrayLikeUtils } from './ArrayLikeUtils';
import { ChannelTimedPaneInterpolation } from './ChannelTimedPaneInterpolation';
import { isMapVizType, preserveArrayType, DataStoreValue, deriveWarpedFraction, getAttributeTiming } from './DataStorePaneStateUtility';
import { DataStorePaths } from './DataStorePaths';
import { IDataStore } from './IDataStore';
import { MapPaneInterpolation } from './MapPaneInterpolation';
import { interpolatePathWithMapping, type PaneInterpolationContext } from './PaneInterpolation';
import { PaneStateAttribute, PaneStateAttributeUtil } from './PaneStateAttribute';
import { SingleFractionPaneInterpolation } from './SingleFractionPaneInterpolation';

const channelTimedPaneInterpolation = new ChannelTimedPaneInterpolation();
const mapPaneInterpolation = new MapPaneInterpolation();
const singleFractionPaneInterpolation = new SingleFractionPaneInterpolation();

/**
 * Represents the state of a visualization pane at a point in time.
 *
 * Wraps an IDataStore with an override mechanism for interpolated values.
 * This design allows us to:
 * - Preserve the original base datastore (immutability)
 * - Store interpolated values separately in the overrides map
 * - Easily reset to base state by clearing overrides
 * - Share the same base datastore across multiple animations
 *
 * Example usage:
 * ```typescript
 * const baseData = {
 *   PDMarks: { '[pane0]': { Encodings: { X: new Float64Array([0, 10]) } } }
 * };
 * const state = new DataStorePaneState(new DataStore(baseData));
 *
 * // Set an override (e.g., during interpolation)
 * state.setOverride('PDMarks.[pane0].Encodings.X', new Float64Array([50, 60]));
 *
 * // getValue checks overrides first, then falls back to base
 * console.log(state.getValue('PDMarks.[pane0].Encodings.X')); // [50, 60] (from override)
 * ```
 */
export class DataStorePaneState implements IPaneState {
  private baseDataStore: IDataStore;
  private overrides: Map<string, DataStoreValue>;
  /** Per-pane TO indices for injected exit marks. */
  private exitingToIndicesMap: Map<string, Set<number>> = new Map();

  /**
   * Create a DataStorePaneState wrapping a base datastore.
   *
   * @param dataStore - The base IDataStore to wrap
   */
  constructor(dataStore: IDataStore) {
    this.baseDataStore = dataStore;
    this.overrides = new Map();
  }

  /**
   * Get a value by path.
   * Checks overrides first, then falls back to base datastore.
   *
   * @param path - Dot-separated path (e.g., 'marks.mark_123.x')
   * @returns The value at that path, or undefined if not found
   */
  getValue(path: string): DataStoreValue {
    if (this.overrides.has(path)) {
      return this.overrides.get(path)!;
    }
    return this.baseDataStore.getValue(path) as DataStoreValue;
  }

  /**
   * Set an override value.
   * This does NOT modify the base datastore.
   *
   * @param path - Dot-separated path
   * @param value - The value to set
   */
  setOverride(path: string, value: DataStoreValue): void {
    this.overrides.set(path, value);
  }

  /**
   * Set per-pane exit TO indices populated by injectExitMarksIntoBase().
   */
  setExitingToIndicesMap(map: Map<string, Set<number>>): void {
    this.exitingToIndicesMap = map;
  }

  getExitingToIndicesMap(): Map<string, Set<number>> {
    return this.exitingToIndicesMap;
  }

  /**
   * Clear all overrides.
   * After calling this, getValue() will return base datastore values.
   */
  clearOverrides(): void {
    this.overrides.clear();
  }

  /**
   * Get the number of overrides currently set.
   *
   * @returns The count of override values
   */
  getOverrideCount(): number {
    return this.overrides.size;
  }

  /**
   * Check if a specific path has an override.
   *
   * @param path - Dot-separated path
   * @returns true if an override exists for this path
   */
  hasOverride(path: string): boolean {
    return this.overrides.has(path);
  }

  /**
   * Get the base datastore (without overrides).
   *
   * @returns The underlying IDataStore
   */
  getBaseDataStore(): IDataStore {
    return this.baseDataStore;
  }

  /**
   * Export current state as a new IDataStore with overrides applied.
   * If alternateBase is provided, overrides are applied onto it instead of the own base.
   *
   * @param alternateBase - Optional alternate base datastore to apply overrides onto
   * @returns A new IDataStore with all current values
   */
  toDataStore(alternateBase?: IDataStore): IDataStore {
    const result = (alternateBase ?? this.baseDataStore).clone();

    this.overrides.forEach((value, path) => {
      result.setValue(path, value);
    });

    return result;
  }

  /**
   * Interpolate this state toward a target state.
   * Sets overrides for all interpolated values.
   *
   *
   * Different attributes use different interpolation strategies:
   * - Linear: Most attributes (X, Y, Size, Width, Height, etc.)
   * - Color: Per-channel ARGB interpolation
   * - Angle: Shortest angular path with 360° wrapping (Theta, Theta1)
   *
   * @param targetState - The target state to interpolate toward
   * @param fraction - Interpolation fraction [0, 1]
   */
  interpolate(
    targetState: DataStorePaneState,
    fraction: number,
    nonMarksTiming?: PaneChannelTiming | null,
    nonMarkAttributeTimingOverrides?: Map<PaneStateAttribute, PaneChannelTiming> | null,
    animatingPaneIds?: Set<string> | null
  ): void {
    // Clear existing overrides before interpolating
    this.clearOverrides();

    // Get all pane IDs from PDMarks keys directly
    // PDMarks structure: { '[paneKey]': { Encodings: {...}, MarkLayoutData: {...} } }
    const paneIds = this.baseDataStore.getAllKeys('PDMarks');

    if (paneIds.length === 0) {
      return; // No panes to interpolate
    }

    // Map scenario: special handling (early return)
    if (isMapVizType(this.baseDataStore)) {
      const mapInterpolationContext = this.createInterpolationContext();
      for (const paneId of paneIds) {
        if (animatingPaneIds && !animatingPaneIds.has(paneId)) {
          continue;
        }
        mapPaneInterpolation.interpolatePane(
          mapInterpolationContext,
          paneId,
          targetState.getValue.bind(targetState) as (path: string) => DataStoreValue,
          fraction,
          targetState.getExitingToIndicesMap().get(paneId)
        );
      }
      return;
    }

    // 1. Interpolate mark attributes within each pane
    const standardInterpolationContext = this.createInterpolationContext();
    for (const paneId of paneIds) {
      if (animatingPaneIds && !animatingPaneIds.has(paneId)) {
        continue;
      }
      singleFractionPaneInterpolation.interpolatePane(
        standardInterpolationContext,
        paneId,
        targetState.getValue.bind(targetState) as (path: string) => DataStoreValue,
        fraction
      );
    }

    // 2. Interpolate pane bounds for cross-pane animation
    this.interpolatePaneExtents(targetState, fraction, nonMarksTiming, nonMarkAttributeTimingOverrides);
  }

  /**
   * Interpolate using per-channel enter/move/exit timing.
   * Derives a local sub-fraction for each mark based on its channel
   * (enter/move/exit) and the resolved timing windows.
   * C++ Reference: MarkAnimationController per-mark FractionWarperAnimation.
   *
   * Falls back to single-fraction interpolation for panes without channel info
   * and for map viz types (which have their own enter/exit logic).
   *
   * @param targetState       - The target state to interpolate toward
   * @param globalFraction    - Overall animation fraction [0, 1]
   * @param paneChannelTimings - Per-pane channel timing + mark ID sets
   */
  interpolateWithChannelTimings(
    targetState: DataStorePaneState,
    globalFraction: number,
    paneChannelTimings: Map<string, PaneChannelTimings>,
    nonMarksTiming?: PaneChannelTiming | null,
    nonMarkAttributeTimingOverrides?: Map<PaneStateAttribute, PaneChannelTiming> | null,
    animatingPaneIds?: Set<string> | null
  ): void {
    this.clearOverrides();

    const paneIds = this.baseDataStore.getAllKeys('PDMarks');
    if (paneIds.length === 0) {
      return;
    }

    if (isMapVizType(this.baseDataStore)) {
      // Map viz: use legacy single fraction (enter/exit handled by injectExitMarks)
      const mapInterpolationContext = this.createInterpolationContext();
      for (const paneId of paneIds) {
        if (animatingPaneIds && !animatingPaneIds.has(paneId)) {
          continue;
        }
        mapPaneInterpolation.interpolatePane(
          mapInterpolationContext,
          paneId,
          targetState.getValue.bind(targetState) as (path: string) => DataStoreValue,
          globalFraction,
          targetState.getExitingToIndicesMap().get(paneId)
        );
      }
      return;
    }

    const standardInterpolationContext = this.createInterpolationContext();
    for (const paneId of paneIds) {
      if (animatingPaneIds && !animatingPaneIds.has(paneId)) {
        continue;
      }
      const channelInfo = paneChannelTimings.get(paneId);
      if (channelInfo) {
        channelTimedPaneInterpolation.interpolatePane(
          standardInterpolationContext,
          paneId,
          targetState.getValue.bind(targetState) as (path: string) => DataStoreValue,
          globalFraction,
          channelInfo,
          targetState.getExitingToIndicesMap().get(paneId)
        );
      } else {
        singleFractionPaneInterpolation.interpolatePane(
          standardInterpolationContext,
          paneId,
          targetState.getValue.bind(targetState) as (path: string) => DataStoreValue,
          globalFraction
        );
      }
    }

    // Pane extents use global fraction; non-marks timing is handled by NonMarksAnimationController
    this.interpolatePaneExtents(targetState, globalFraction, nonMarksTiming, nonMarkAttributeTimingOverrides);
  }

  private interpolatePaneExtents(
    targetState: DataStorePaneState,
    fraction: number,
    nonMarksTiming?: PaneChannelTiming | null,
    nonMarkAttributeTimingOverrides?: Map<PaneStateAttribute, PaneChannelTiming> | null
  ): void {
    const paneIndexMapping = this.buildPaneIndexMapping(targetState);
    const interpolationContext = this.createInterpolationContext();
    const activeOverrideTimings = nonMarkAttributeTimingOverrides ?? new Map<PaneStateAttribute, PaneChannelTiming>();

    // Interpolate all pane bounds attributes
    // C++ Reference: GetOverallAttributesForAnimation() case Panes returns GetAllNonMarkAttributes()
    const paneBoundsAttributes = PaneStateAttributeUtil.getAllPaneBoundsAttributes();

    for (const attribute of paneBoundsAttributes) {
      // Pane bounds paths don't include paneId (they're global arrays indexed by pane position)
      const path = DataStorePaths.getKeyPathForAttribute('', attribute);
      if (!path) {
        continue;
      }

      const effectiveFraction =
        nonMarksTiming != null
          ? deriveWarpedFraction(fraction, getAttributeTiming(attribute, nonMarksTiming, activeOverrideTimings))
          : fraction;
      interpolatePathWithMapping(
        interpolationContext,
        targetState.getValue.bind(targetState) as (path: string) => DataStoreValue,
        path,
        attribute,
        effectiveFraction,
        paneIndexMapping
      );
    }
  }

  private buildPaneIndexMapping(targetState: DataStorePaneState): Map<number, number> {
    const mapping = new Map<number, number>();

    // Build mappings for both states
    const fromMarkIdToPaneIndex = this.buildAllMarkIdToPaneIndexMappings(this);
    const toMarkIdToPaneIndex = this.buildAllMarkIdToPaneIndexMappings(targetState);

    // For marks in both states, map FROM pane_index → TO pane_index
    for (const [compositeKey, fromPaneIndex] of Array.from(fromMarkIdToPaneIndex.entries())) {
      const toPaneIndex = toMarkIdToPaneIndex.get(compositeKey);
      if (toPaneIndex !== undefined) {
        // Direct mapping: each FROM pane_index maps to exactly one TO pane_index
        mapping.set(fromPaneIndex, toPaneIndex);
      }
    }

    return mapping;
  }

  /**
   * Build composite key→pane_index mapping for all panes in a state.
   *
   * @param state - The state to build mappings for
   * @returns Map of composite key (paneId:markId) → pane_index
   */
  private buildAllMarkIdToPaneIndexMappings(state: DataStorePaneState): Map<string, number> {
    const paneIdMarkIdCompositeKeyToPaneIndex = new Map<string, number>();
    const paneIds = state.baseDataStore.getAllKeys('PDMarks');
    for (const paneId of paneIds) {
      state.buildPaneIdMarkIdToPaneIndexMap(paneId, paneIdMarkIdCompositeKeyToPaneIndex);
    }
    return paneIdMarkIdCompositeKeyToPaneIndex;
  }

  private determineRowColFields(
    paneId: string,
    state: DataStorePaneState
  ): { rowField: 'pane_row' | 'user_pane_row'; colField: 'pane_col' | 'user_pane_col' } {
    const userPaneRows = ArrayLikeUtils.toArray(state.getValue(`PDMarks.${paneId}.Encodings.user_pane_row`));
    const userPaneCols = ArrayLikeUtils.toArray(state.getValue(`PDMarks.${paneId}.Encodings.user_pane_col`));
    const uniqueUserRows = new Set(userPaneRows).size;
    const uniqueUserCols = new Set(userPaneCols).size;
    return {
      rowField: uniqueUserRows > 1 ? 'user_pane_row' : 'pane_row',
      colField: uniqueUserCols > 1 ? 'user_pane_col' : 'pane_col',
    };
  }

  /**
   * Build paneId:MarkId composite key → pane_index mapping for a specific pane using C++ calculation approach.
   *
   * Mirrors C++ PageIntervalTableHelper::TraverseMarkIdsForPerPaneExtentsTableInfo():
   * - Reads PaneRow, PaneCol, PaneLayer encodings
   * - Calculates pane index: row * columnCount + col + layer * rowCount * columnCount
   * - Creates composite key "paneId:markId" to prevent MarkId collisions across panes
   *
   * @param paneId - The pane descriptor ID
   * @param outMap - Output map to populate with paneId:markId → pane_index
   */
  buildPaneIdMarkIdToPaneIndexMap(paneId: string, outMap: Map<string, number>): void {
    const markIds = ArrayLikeUtils.toArray(this.getValue(`PDMarks.${paneId}.Encodings.MarkId`));
    if (markIds.length === 0) {
      return;
    }

    const { rowField, colField } = this.determineRowColFields(paneId, this);
    const paneRows = ArrayLikeUtils.toArray(this.getValue(`PDMarks.${paneId}.Encodings.${rowField}`));
    const paneCols = ArrayLikeUtils.toArray(this.getValue(`PDMarks.${paneId}.Encodings.${colField}`));
    const paneLayers = ArrayLikeUtils.toArray(this.getValue(`PDMarks.${paneId}.Encodings.pane_layer`));

    // All arrays must be present and same length
    if (paneRows.length > 0 && paneRows.length === paneCols.length && paneRows.length === paneLayers.length) {
      const columnCount = this.getNumPaneColumns();
      const rowCount = this.getNumPaneRows();

      for (let i = 0; i < Math.min(markIds.length, paneRows.length); i++) {
        const markId = markIds[i];
        const row = paneRows[i];
        const col = paneCols[i];
        const layer = paneLayers[i];

        if (typeof markId === 'number' && typeof row === 'number' && typeof col === 'number' && typeof layer === 'number') {
          // C++ Reference: PageIntervalTableHelper::TraverseMarkIdsForPerPaneExtentsTableInfo()
          const paneIndex = row * columnCount + col + layer * rowCount * columnCount;
          const paneIdMarkIdCompositeKey = `${paneId}:${markId}`;
          outMap.set(paneIdMarkIdCompositeKey, paneIndex);
        }
      }
    }
  }

  private getNumPaneRows(): number {
    return this.getNumPaneDimensions('pane_row');
  }

  getNumPaneColumns(): number {
    return this.getNumPaneDimensions('pane_col');
  }

  private getNumPaneDimensions(dimension: 'pane_row' | 'pane_col'): number {
    const values = ArrayLikeUtils.toArray(this.getValue(`Viewpoint.PerPaneExtents.${dimension}`));
    if (values.length === 0) {
      return 1;
    }
    const maxValue = Math.max(...values.filter((v) => typeof v === 'number'));
    return maxValue + 1;
  }

  /**
   * Build an index mapping from FROM indices to TO indices based on MarkId matching.
   *
   * @param fromMarkIds - MarkId array from FROM state (array, TypedArray, or object dictionary)
   * @param toMarkIds - MarkId array from TO state (array, TypedArray, or object dictionary)
   * @returns Map from FROM index to TO index (only for marks in both states)
   */
  private buildMarkIdIndexMapping(fromMarkIds: DataStoreValue, toMarkIds: DataStoreValue): Map<number, number> {
    const indexMapping = new Map<number, number>();

    // If MarkId arrays are not available, return empty mapping (will fall back to index-based)
    if (!fromMarkIds || !toMarkIds) {
      return indexMapping;
    }

    // Use ArrayLikeUtils to handle all formats (arrays, TypedArrays, object dictionaries)
    const fromArray = ArrayLikeUtils.toArray(fromMarkIds);
    const toArray = ArrayLikeUtils.toArray(toMarkIds);

    // If conversion failed, return empty mapping
    if (fromArray.length === 0 || toArray.length === 0) {
      return indexMapping;
    }

    // Build TO MarkId -> index lookup
    const toMarkIdToIndex = new Map<number, number>();
    for (let i = 0; i < toArray.length; i++) {
      toMarkIdToIndex.set(toArray[i], i);
    }

    // Map FROM indices to TO indices based on MarkId
    for (let fromIndex = 0; fromIndex < fromArray.length; fromIndex++) {
      const markId = fromArray[fromIndex];
      const toIndex = toMarkIdToIndex.get(markId);
      if (toIndex !== undefined) {
        indexMapping.set(fromIndex, toIndex);
      }
    }

    return indexMapping;
  }

  private createInterpolationContext(outState: DataStorePaneState = this): PaneInterpolationContext {
    return {
      getValue: this.getValue.bind(this) as (path: string) => DataStoreValue,
      setOverride: outState.setOverride.bind(outState) as (path: string, value: DataStoreValue) => void,
      buildMarkIdIndexMapping: this.buildMarkIdIndexMapping.bind(this) as (
        fromMarkIds: DataStoreValue,
        toMarkIds: DataStoreValue
      ) => Map<number, number>,
    };
  }

  /**
   * Create a deep copy of this pane state.
   * C++ Reference: DataStorePaneState::CreateCopy()
   *
   * @returns A new DataStorePaneState with cloned base datastore and copied overrides
   */
  createCopy(): DataStorePaneState {
    const copy = new DataStorePaneState(this.baseDataStore.clone());
    this.overrides.forEach((value, path) => {
      copy.setOverride(path, value);
    });
    return copy;
  }

  /**
   * Used for non-mark attributes (grid lines, axis labels, scene margins, tick placement)
   * C++ Reference: DataStorePaneState::RampAttributesInto()
   * For arrays: uses identity index mapping (each TO index maps to same FROM index),
   * For scalars: directly lerps or color-interpolates the value.
   *
   * @param otherState - The target (TO) state to interpolate toward
   * @param fraction - Interpolation fraction [0, 1]
   * @param attributes - The non-mark attributes to interpolate (e.g. AxisOutput, GridLines)
   * @param outState - Output state that receives the interpolated overrides
   */
  rampAttributesInto(
    otherState: DataStorePaneState,
    fraction: number,
    attributes: PaneStateAttribute[],
    outState: DataStorePaneState
  ): void {
    const interpolationContext = this.createInterpolationContext(outState);
    for (const attribute of attributes) {
      const path = DataStorePaths.getKeyPathForAttribute('', attribute);
      if (!path) {
        continue;
      }
      interpolatePathWithMapping(
        interpolationContext,
        otherState.getValue.bind(otherState) as (path: string) => DataStoreValue,
        path,
        attribute,
        fraction,
        new Map<number, number>()
      );
    }
  }

  /**
   * Set the alpha channel of a color attribute to 0 (fully transparent).
   * Used to create the mid-state of grid line / axis color animations:
   * when grid lines change between FROM and TO (different count or position),
   * the midpoint state fades both FROM and TO lines to invisible so they
   * appear to fade out then fade in rather than jumping.
   * C++ Reference: NonMarksAnimationController::SetMiddleAlphasString()
   *
   * @param path - Path to the color attribute
   */
  setColorTransparent(path: string): void {
    const value = this.getValue(path);
    if (value === undefined || value === null) {
      return;
    }

    if (typeof value === 'number') {
      // Single color value: clear alpha channel (set A to 0)
      // The bit mask & 0x00ffffff directly overwrites the alpha byte to 0x00
      // in the ARGB integer, making the color fully transparent
      const transparent = (value & 0x00ffffff) >>> 0;
      this.setOverride(path, transparent);
    } else {
      // Array of color values: clear alpha for each
      const arr = ArrayLikeUtils.toArray(value);
      if (arr.length > 0) {
        const transparentArr = arr.map((c) => (typeof c === 'number' ? (c & 0x00ffffff) >>> 0 : c));
        this.setOverride(path, preserveArrayType(value, transparentArr));
      }
    }
  }
}
